import { useBreakpoint } from "@povio/ui";
import { useRouter } from "next/router";
import { useNextStep } from "nextstepjs";
import { useCallback, useEffect, useMemo } from "react";

import { RouteConfig } from "@/config/route.config";
import {
  DEMO_TOUR_ID,
  DEMO_TOUR_MOBILE_ID,
  type HighlightStyles,
  demoTourMobileSteps,
  demoTourSteps,
} from "@/config/tour.config";
import { AuthContext } from "@/data/auth/auth.context";
import { ProfileQueries } from "@/data/profile/profile.queries";
import { logger } from "@/util/logger";

/**
 * Converts a value to a CSS string (adds 'px' suffix if it's a number)
 */
const toCssValue = (value: string | number): string => {
  return typeof value === "number" ? `${value}px` : value;
};

/**
 * Applies highlight styles to a tour element
 * @param element - The HTML element to highlight
 * @param highlight - Optional custom highlight styles (padding, margin, etc.)
 */
const applyHighlightStyles = (element: HTMLElement, highlight?: HighlightStyles) => {
  /* eslint-disable no-param-reassign */
  // Use box-shadow with inset for border + spread for padding + separate shadow for glow
  element.style.boxShadow = "0 0 0 3px #3B82F6, 0 0 21px 0 #3B82F6";
  element.style.borderRadius = toCssValue(highlight?.borderRadius ?? "8px");
  element.style.position = "relative";
  element.style.transition = "box-shadow 0.3s ease-in-out, padding 0.3s ease-in-out, margin 0.3s ease-in-out";

  if (highlight?.padding) {
    element.style.padding = toCssValue(highlight.padding);
  }
  if (highlight?.margin) {
    element.style.margin = toCssValue(highlight.margin);
  }
  /* eslint-enable no-param-reassign */
};

/**
 * Removes highlight styles from a tour element
 */
const removeHighlightStyles = (element: HTMLElement) => {
  /* eslint-disable no-param-reassign */
  element.style.boxShadow = "";
  element.style.borderRadius = "";
  element.style.position = "";
  element.style.transition = "";
  element.style.padding = "";
  element.style.margin = "";
  /* eslint-enable no-param-reassign */
};

/**
 * Custom hook to manage the demo tour
 *
 * Provides convenient methods for starting, skipping, and tracking
 * the completion status of the tour. Also handles custom highlighting
 * of the current tour element.
 */
export const useTour = () => {
  const router = useRouter();
  const { startNextStep, currentStep, currentTour } = useNextStep();
  const isDesktop = useBreakpoint("md");
  const { isLoggedIn } = AuthContext.useAuth();

  const { isLoading: isProfileLoading } = ProfileQueries.useGetMy({
    enabled: isLoggedIn,
  });

  const isTourActive = useMemo(
    () => currentTour === DEMO_TOUR_ID || currentTour === DEMO_TOUR_MOBILE_ID,
    [currentTour],
  );

  // Get the appropriate tour steps based on whether we're on mobile or desktop
  const activeTourSteps = useMemo(() => {
    if (currentTour === DEMO_TOUR_MOBILE_ID) {
      return demoTourMobileSteps;
    }
    return demoTourSteps;
  }, [currentTour]);

  /**
   * Effect to add/remove highlight to the current tour element
   */
  useEffect(() => {
    // Get all selectors from both desktop and mobile tours for cleanup
    const allDesktopSelectors = demoTourSteps.map((step) => step.selector).filter((s): s is string => Boolean(s));
    const allMobileSelectors = demoTourMobileSteps.map((step) => step.selector).filter((s): s is string => Boolean(s));
    const allSelectors = [...new Set([...allDesktopSelectors, ...allMobileSelectors])];

    // Remove highlight from all previous elements
    allSelectors.forEach((selector) => {
      const element = document.querySelector(selector) as HTMLElement | null;
      if (element) {
        removeHighlightStyles(element);
      }
    });

    // Add highlight to current element if tour is active
    if (isTourActive && currentStep >= 0 && currentStep < activeTourSteps.length) {
      const stepConfig = activeTourSteps[currentStep];
      if (stepConfig?.selector && !stepConfig.preventHighlight) {
        const element = document.querySelector(stepConfig.selector) as HTMLElement | null;
        if (element) {
          // Apply custom highlight styles if configured
          applyHighlightStyles(element, stepConfig.highlight);
        } else {
          logger.warn("Tour element not found:", stepConfig.selector);
        }
      }
    }

    // Cleanup function to remove all highlights when component unmounts or tour ends
    return () => {
      allSelectors.forEach((selector) => {
        const element = document.querySelector(selector) as HTMLElement | null;
        if (element) {
          removeHighlightStyles(element);
        }
      });
    };
  }, [currentStep, isTourActive, activeTourSteps]);

  /**
   * Effect to scroll to top when reaching YourNumbersSection step
   * This step may trigger navigation, so we need a small delay to ensure
   * navigation and page render complete before scrolling
   */
  useEffect(() => {
    const hasScrollToTop = activeTourSteps[currentStep]?.scrollToTop;

    if (isTourActive && hasScrollToTop) {
      // Small delay to ensure navigation and page render complete
      const scrollTimer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 10);

      return () => clearTimeout(scrollTimer);
    }

    return undefined;
  }, [currentStep, isTourActive, activeTourSteps]);

  /**
   * Start the demo tour (uses appropriate config based on screen size)
   */
  const startTour = useCallback(() => {
    const tourId = isDesktop ? DEMO_TOUR_ID : DEMO_TOUR_MOBILE_ID;
    startNextStep(tourId);
  }, [startNextStep, isDesktop]);

  /**
   * Mark the tour as completed
   * This is called when the tour is skipped or finished naturally
   * Prevents the tour from auto-starting on subsequent visits
   */
  const markTourCompleted = () => {
    router.push(RouteConfig.Home);
  };

  /**
   * Reset the tour completion status
   * Useful for testing or allowing users to retake the tour
   */
  const resetTour = useCallback(() => {
    // No-op: Resetting tour status now requires backend intervention (profile reset)
    logger.info("Tour reset requested but handled by backend profile reset");
  }, []);

  /**
   * Automatically start the tour after a delay if not completed
   * Waits for the first tour element to be mounted before starting
   * Retries up to maxRetries times if element isn't found
   * Works on both desktop and mobile devices with appropriate tour configuration
   * Will not restart the tour if it's already running
   * @param delayMs - Delay in milliseconds before starting the tour (default: 1000ms)
   * @param maxRetries - Maximum number of retries to wait for element (default: 10)
   */
  const autoStartTour = useCallback(
    (delayMs: number = 1000, maxRetries: number = 10) => {
      // Don't start if already active
      if (isTourActive) {
        return;
      }

      // Use appropriate tour config based on screen size
      const tourSteps = isDesktop ? demoTourSteps : demoTourMobileSteps;
      const tourId = isDesktop ? DEMO_TOUR_ID : DEMO_TOUR_MOBILE_ID;

      let retryCount = 0;

      const tryStartTour = () => {
        const firstStepSelector = tourSteps[0]?.selector;

        // If first step has no selector (e.g., welcome step), start immediately
        if (!firstStepSelector) {
          logger.info("Tour starting - first step has no selector");
          startNextStep(tourId);
          return;
        }

        const element = document.querySelector(firstStepSelector);
        if (element) {
          logger.info("Tour starting - element found:", firstStepSelector);
          startNextStep(tourId);
        } else {
          retryCount++;
          if (retryCount < maxRetries) {
            logger.info(`Tour element not ready, retrying (${retryCount}/${maxRetries})...`);
            setTimeout(tryStartTour, 500); // Retry every 500ms
          } else {
            logger.warn("Tour auto-start failed: element not found after retries", firstStepSelector);
          }
        }
      };

      setTimeout(tryStartTour, delayMs);
    },
    [isDesktop, startNextStep, isTourActive],
  );

  /**
   * Start the tour with navigation handling
   * If user is not on the profile page, navigate there first before starting
   * Works for both regular and demo mode
   */
  const startTourWithNavigation = useCallback(() => {
    const profilePath = RouteConfig.Demo.Profile.slice(0, -1); // Remove trailing slash
    const currentPath = router.pathname;

    if (currentPath !== profilePath) {
      logger.info("Navigating to profile before starting tour");
      router.push(RouteConfig.Demo.Profile).then(() => {
        // Wait for navigation and DOM to be ready
        setTimeout(() => {
          startTour();
        }, 500);
      });
    } else {
      startTour();
    }
  }, [router, startTour]);

  return {
    // Tour control methods
    startTour,
    startTourWithNavigation,
    autoStartTour,

    // Tour status management
    markTourCompleted,
    resetTour,

    // Tour status values
    isTourStatusLoading: isProfileLoading,

    // Tour state
    currentTour,
    currentStep,

    isTourActive,
  };
};
